import { notifySimple } from "../../../themes/components/Notifications";
import Form from "./Form";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "../../../components/Icons/DeleteIcon";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Button from "@mui/material/Button";
import { useRef, useState } from "react";
import { COLORS } from "../../../themes/Base";
import Tooltip from "@mui/material/Tooltip";

type Props = {
  questions: ProjectSupplierQuestion[];
  name: string;
  updateQuestions: (fieldName: string, questions: ProjectSupplierQuestion[]) => void;
};

const SupplierQuestions: React.FC<Props> = ({ questions, name, updateQuestions }) => {
  const [newQuestion, setNewQuestion] = useState<ProjectSupplierQuestion | null>(null);
  const [questionError, setQuestionError] = useState<Record<string, string>>({});

  const validateQuestion = (question: ProjectSupplierQuestion): boolean => {
    if (!question.title || question.title.length < 3 || question.title.length > 255) {
      // notifySimple(["The question title must be between 3 and 255 characters"], "error");
      setQuestionError((prev) => ({
        ...prev,
        [question.id]: "The question title must be between 3 and 255 characters",
      }));
      return false;
    }

    if (!question.number) {
      notifySimple(["The question number is required"], "error");
      return false;
    }

    setQuestionError((prev) => {
      const copy = { ...prev };
      delete copy[question.id];
      return copy;
    });

    const doesQuestionExist = questions.find(function (obj) {
      return obj.number == question.number;
    });

    if (
      doesQuestionExist !== undefined &&
      doesQuestionExist.id !== question.id &&
      doesQuestionExist.deleted === false
    ) {
      notifySimple([`Question number ${question.number} already exists`], "error");
      return false;
    }

    return true;
  };

  const addQuestionToArray = (): void => {
    // Prevent adding if the last question is empty and not deleted
    const last = sortedQuestions[sortedQuestions.length - 1];
    if (last && (!last.title || last.title.trim().length < 3)) {
      notifySimple(["Please fill in the last question before adding a new one."], "error");
      return;
    }
    const newQuestion: ProjectSupplierQuestion = {
      id: window.crypto.randomUUID(),
      number: sortedQuestions.length + 1,
      title: "",
      expected_answer: "",
      terminate_on_incorrect: false,
      deleted: false,
    };
    setNewQuestion(newQuestion);
  };

  const updateQuestionArray = (question: ProjectSupplierQuestion): void => {
    if (!validateQuestion(question)) {
      return;
    }

    let questionsCopy: ProjectSupplierQuestion[] = [...questions];

    // Delete the existing copy of the question
    questionsCopy = questionsCopy.filter(function (obj) {
      return obj.id !== question.id;
    });

    // Add it back with updated data
    questionsCopy.push(question);
    updateQuestions(name, questionsCopy);
    // Reset the new question state if this was the new question that was added
    if (newQuestion && newQuestion.id == question.id) {
      setNewQuestion(null);
    }
  };

  const deleteQuestion = (e: React.MouseEvent<HTMLButtonElement>): void => {
    const questionId = e.currentTarget.dataset.id;

    if (!questionId) {
      notifySimple([`No question id detected`], "error");
      return;
    }

    let questionsCopy: ProjectSupplierQuestion[] = [...questions];

    // Get a copy of the question
    const question = questionsCopy.find(function (obj) {
      return obj.id === questionId;
    });

    if (!question) {
      notifySimple([`No question found for that id`], "error");
      return;
    }

    // Set to deleted
    question.deleted = true;

    // Delete the existing copy of the question
    questionsCopy = questionsCopy.filter(function (obj) {
      return obj.id !== question.id;
    });

    // Add it back with updated data
    questionsCopy.push(question);
    updateQuestions(name, questionsCopy);
    notifySimple([`Question ${question.number} deleted`], "success");
  };

  const sortQuestions = () => {
    return questions.sort((a, b) => a.number - b.number);
  };

  const moveQuestion = (fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= questions.length) return;
    const sorted = sortQuestions().filter(q => !q.deleted);
    const updated = [...sorted];
    const [moved] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, moved);
    // Re-number after move
    const renumbered = updated.map((q, idx) => ({ ...q, number: idx + 1 }));
    // Add back deleted questions
    const deleted = questions.filter(q => q.deleted);
    updateQuestions(name, [...renumbered, ...deleted]);
  };

  // Drag state
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const dragItemNode = useRef<HTMLDivElement | null>(null);

  const sortedQuestions = sortQuestions().filter(q => !q.deleted);

  // Handle drag start
  const handleDragStart = (idx: number, e: React.DragEvent<HTMLDivElement>) => {
    setDraggedIdx(idx);
    dragItemNode.current = e.currentTarget;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
  };

  // Handle drag enter
  const handleDragEnter = (idx: number) => {
    setDragOverIdx(idx);
  };

  // Handle drag end
  const handleDragEnd = () => {
    if (draggedIdx !== null && dragOverIdx !== null && draggedIdx !== dragOverIdx) {
      moveQuestion(draggedIdx, dragOverIdx);
    }
    setDraggedIdx(null);
    setDragOverIdx(null);
    dragItemNode.current = null;
  };

  return (
    <div>
      <Typography variant="h3" sx={{ p: 0 }}>Roundtable AI</Typography>
      <Typography variant="subtitle1">
        The questions below will be used to evaluate the respondent before they enter the survey.
      </Typography>
      {questions.length ? (
        sortQuestions().map((question, idx) =>
          question.deleted === false ? (
            <Box
              key={question.id}
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1.5px solid #E4E8D3",
                borderRadius: "16px",
                mb: 2,
                px: 2,
                py: 1.5,
                background: dragOverIdx === idx && draggedIdx !== null ? "#f0f4ff" : "#fff",
                position: "relative",
                opacity: draggedIdx === idx ? 0.5 : 1,
                cursor: "grab",
                transition: "background 0.2s",
              }}
              draggable
              onDragStart={(e) => handleDragStart(idx, e)}
              onDragEnter={() => draggedIdx !== null && handleDragEnter(idx)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => e.preventDefault()}
            >
              <DragIndicatorIcon sx={{ color: "#67676C", mr: 2 }} />
              <Typography variant="h3" sx={{ mr: 2, p: 0 }}>Q{question.number}.</Typography>
              <Box sx={{ flex: 1 }}>
                <Form
                  handleSubmit={updateQuestionArray}
                  questionData={question}
                  error={questionError[question.id]}
                />
              </Box>
              <Tooltip title="Delete" placement="top">
                <IconButton onClick={deleteQuestion} data-id={question.id}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null
        )
      ) : (
        <Typography variant="subtitle1">No questions to display</Typography>
      )}
      {newQuestion && (
        <Box
          key={newQuestion.id}
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1.5px solid #E4E8D3",
            borderRadius: "16px",
            mb: 2,
            px: 2,
            py: 1.5,
            position: "relative",
            cursor: "grab",
            transition: "background 0.2s",
          }}
        >
          <Typography variant="h3" sx={{ mr: 2, p: 0 }}>Q{newQuestion.number}.</Typography>
          <Box sx={{ flex: 1 }}>
            <Form
              handleSubmit={updateQuestionArray}
              questionData={newQuestion}
              error={questionError[newQuestion.id]}
            />
          </Box>
          <Tooltip title="Delete" placement="top">
            <IconButton onClick={() => setNewQuestion(null)} data-id={newQuestion.id}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Button
        variant="contained"
        onClick={addQuestionToArray}
        sx={{
          mt: 1,
          background: "#EAEEFF",
          color: COLORS.secondary,
          borderRadius: "12px",
          fontWeight: 400,
          fontSize: 14,
          boxShadow: "none",
          textTransform: "none",
          "&:hover": {
            background: "#dbe3fd",
            boxShadow: "none",
          },
        }}
        endIcon={<AddIcon sx={{ color: COLORS.secondary }} />}
      >
        Add Question
      </Button>
    </div>
  );
};

export default SupplierQuestions;
